import { useCallback, useEffect, useRef, useState } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { api } from '../api.js';
import type { RuntimeBucket, RuntimeHistory } from '../api.js';
import { useOutletContext } from 'react-router';
import type { ShellContext } from './AppShell.js';
import { t } from '../i18n.js';

type Range = '12m' | '5y' | 'all';

const BUCKETS: { key: RuntimeBucket; labelKey: string }[] = [
  { key: 'day', labelKey: 'runtimes.bucket_day' },
  { key: 'week', labelKey: 'runtimes.bucket_week' },
  { key: 'month', labelKey: 'runtimes.bucket_month' },
  { key: 'year', labelKey: 'runtimes.bucket_year' },
];

const RANGES: { key: Range; labelKey: string; days: number }[] = [
  { key: '12m', labelKey: 'runtimes.range_12m', days: 365 },
  { key: '5y', labelKey: 'runtimes.range_5y', days: 5 * 365 },
  { key: 'all', labelKey: 'runtimes.range_all', days: 50 * 365 },
];

function ymd(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Convert the bucket label coming from SQL into a unix-second tick for
// uPlot's time-aware x axis. Day = YYYY-MM-DD, month = YYYY-MM, year = YYYY,
// week = YYYY-Www (ISO).
function bucketToTs(bucket: string, kind: RuntimeBucket): number {
  if (kind === 'day') return Date.UTC(+bucket.slice(0, 4), +bucket.slice(5, 7) - 1, +bucket.slice(8, 10)) / 1000;
  if (kind === 'month') return Date.UTC(+bucket.slice(0, 4), +bucket.slice(5, 7) - 1, 1) / 1000;
  if (kind === 'year') return Date.UTC(+bucket, 0, 1) / 1000;
  // ISO week: YYYY-Www → Monday of that ISO week.
  const year = +bucket.slice(0, 4);
  const week = +bucket.slice(6, 8);
  // Jan 4 is always in ISO week 1; back off to Monday.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = (jan4.getUTCDay() + 6) % 7; // Mon=0
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - jan4Dow + (week - 1) * 7);
  return monday.getTime() / 1000;
}

function formatBucketLabel(ts: number, kind: RuntimeBucket): string {
  const d = new Date(ts * 1000);
  if (kind === 'year') return String(d.getUTCFullYear());
  if (kind === 'month') return `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function RuntimeView() {
  const { onOpenSettings } = useOutletContext<ShellContext>();
  const ref = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  const [bucket, setBucket] = useState<RuntimeBucket>('month');
  const [range, setRange] = useState<Range>('12m');
  const [history, setHistory] = useState<RuntimeHistory | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [xRange, setXRange] = useState<{ fromTs: number; toTs: number } | null>(null);

  const load = useCallback(async () => {
    const r = RANGES.find((x) => x.key === range)!;
    const today = new Date();
    const from = new Date(today);
    from.setUTCDate(from.getUTCDate() - r.days);
    setXRange({ fromTs: Math.floor(from.getTime() / 1000), toTs: Math.floor(today.getTime() / 1000) });
    try {
      const h = await api.getRuntimeHistory(bucket, ymd(from), ymd(today));
      setHistory(h);
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [bucket, range]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const mount = ref.current;
    if (!mount || !history) return;
    plotRef.current?.destroy();

    if (history.points.length === 0) return;

    const xs = history.points.map((p) => bucketToTs(p.bucket, history.bucket));
    const hours = history.points.map((p) => p.hours);
    const data: uPlot.AlignedData = [xs, hours] as unknown as uPlot.AlignedData;

    const opts: uPlot.Options = {
      width: mount.clientWidth,
      height: Math.max(360, Math.min(560, window.innerHeight - 220)),
      scales: {
        x: xRange ? { time: true, range: [xRange.fromTs, xRange.toTs] } : { time: true },
        y: { auto: true },
      },
      series: [
        {},
        {
          label: t('runtimes.axis_hours'),
          stroke: '#e55f1c',
          fill: 'rgba(229,95,28,0.28)',
          width: 1,
          paths: barsPath(history.points.length),
          points: { show: false },
        },
      ],
      axes: [
        {
          stroke: '#777',
          grid: { stroke: '#f0f0f0', width: 1 },
          values: (_u, splits) => splits.map((s) => formatBucketLabel(s, history.bucket)),
        },
        {
          stroke: '#777',
          grid: { stroke: '#f0f0f0', width: 1 },
          values: (_u, vals) => vals.map((v) => `${v} h`),
        },
      ],
      legend: { show: false },
    };
    plotRef.current = new uPlot(opts, data, mount);

    const ro = new ResizeObserver(() => {
      if (plotRef.current) plotRef.current.setSize({ width: mount.clientWidth, height: plotRef.current.height });
    });
    ro.observe(mount);

    return () => {
      ro.disconnect();
      plotRef.current?.destroy();
      plotRef.current = null;
    };
  }, [history]);

  const refresh = async () => {
    setBusy(true);
    try {
      await api.refreshRuntimes();
      await load();
      setErr(null);
    } catch {
      setErr(t('runtimes.refresh_failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app">
      <header className="shell-header is-standalone">
        <div><span className="updated">{t('runtimes.title')}</span></div>
        <div className="right">
          <button
            className="refresh-pill"
            onClick={() => void refresh()}
            disabled={busy}
          ><span className="icon">↻</span> {busy ? t('runtimes.refreshing') : t('runtimes.refresh')}</button>
          <button className="hero-gear" onClick={onOpenSettings} aria-label={t('header.settings')}>⚙</button>
        </div>
      </header>
      {err && <div className="banner">{err}</div>}
      <div className="app-body">
        <div className="chart-card">
          <div className="chart-head">
            <div className="chart-pills">
              {BUCKETS.map((b) => (
                <button
                  key={b.key}
                  className={`chart-pill${b.key === bucket ? ' is-active' : ''}`}
                  onClick={() => setBucket(b.key)}
                >{t(b.labelKey)}</button>
              ))}
            </div>
            <div className="chart-pills">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  className={`chart-pill${r.key === range ? ' is-active' : ''}`}
                  onClick={() => setRange(r.key)}
                >{t(r.labelKey)}</button>
              ))}
            </div>
          </div>
          <div className="chart-mount" ref={ref} />
          {history && history.points.length === 0 && (
            <div className="muted" style={{ marginTop: 12, fontSize: 13 }}>{t('runtimes.empty')}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// uPlot bars path builder. Pure visual, ~one bar per data point with a
// gap that scales with point density.
function barsPath(n: number): uPlot.Series.PathBuilder {
  return (u, seriesIdx) => {
    const s = u.series[seriesIdx];
    if (!s) return null;
    const xData = u.data[0] as readonly number[];
    const yData = u.data[seriesIdx] as readonly (number | null)[];
    const path = new Path2D();
    if (xData.length < 1) return { stroke: path, fill: path };

    const xRange = u.scales.x;
    const fullWidth = xRange && xRange.max != null && xRange.min != null
      ? u.valToPos(xRange.max, 'x') - u.valToPos(xRange.min, 'x')
      : u.bbox.width;
    const barWidth = Math.max(2, (fullWidth / Math.max(n, 1)) * 0.7);
    const y0 = u.valToPos(0, s.scale ?? 'y', true);
    for (let i = 0; i < xData.length; i++) {
      const y = yData[i];
      if (y == null) continue;
      const cx = u.valToPos(xData[i]!, 'x', true);
      const cy = u.valToPos(y, s.scale ?? 'y', true);
      path.rect(cx - barWidth / 2, Math.min(cy, y0), barWidth, Math.abs(cy - y0));
    }
    return { stroke: path, fill: path };
  };
}
